import { create } from 'zustand'

type Difficulty = 'easy' | 'medium' | 'hard'

interface DifficultyRecord {
  easy: number
  medium: number
  hard: number
}

export interface StatsStore {
  // Career stats (persisted)
  gamesPlayed: number
  wins: DifficultyRecord
  losses: DifficultyRecord
  currentStreak: number
  bestStreak: number
  totalHits: number
  totalShots: number

  // Per-match stats (ephemeral)
  matchTurns: number
  matchHits: number
  matchShots: number
  matchTorpedoesUsed: number
  matchSonarUsed: number
  matchShipsLost: number
  matchFirstBlood: 'player' | 'opponent' | null
  matchDifficulty: Difficulty

  // Difficulty unlock
  unlockedDifficulties: Set<Difficulty>

  // Actions
  recordShot: (hit: boolean) => void
  recordTurn: () => void
  recordChargeUsed: (type: 'torpedo' | 'sonar') => void
  recordShipLost: () => void
  recordFirstBlood: (who: 'player' | 'opponent') => void
  recordGameEnd: (won: boolean, difficulty: Difficulty) => void
  resetMatch: (difficulty: Difficulty) => void
  checkUnlocks: () => void
}

const STORAGE_KEY = 'fleetduel-stats'

function loadCareerStats() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      return {
        gamesPlayed: data.gamesPlayed || 0,
        wins: data.wins || { easy: 0, medium: 0, hard: 0 },
        losses: data.losses || { easy: 0, medium: 0, hard: 0 },
        currentStreak: data.currentStreak || 0,
        bestStreak: data.bestStreak || 0,
        totalHits: data.totalHits || 0,
        totalShots: data.totalShots || 0,
      }
    }
  } catch {}
  return {
    gamesPlayed: 0,
    wins: { easy: 0, medium: 0, hard: 0 },
    losses: { easy: 0, medium: 0, hard: 0 },
    currentStreak: 0,
    bestStreak: 0,
    totalHits: 0,
    totalShots: 0,
  }
}

function computeUnlocks(wins: DifficultyRecord): Set<Difficulty> {
  const unlocked = new Set<Difficulty>(['easy'])
  if (wins.easy >= 1) unlocked.add('medium')
  if (wins.medium >= 3) unlocked.add('hard')
  return unlocked
}

function persistCareer(state: StatsStore) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      gamesPlayed: state.gamesPlayed,
      wins: state.wins,
      losses: state.losses,
      currentStreak: state.currentStreak,
      bestStreak: state.bestStreak,
      totalHits: state.totalHits,
      totalShots: state.totalShots,
    }))
  } catch {}
}

const career = loadCareerStats()

export const useStatsStore = create<StatsStore>((set, get) => ({
  ...career,

  matchTurns: 0,
  matchHits: 0,
  matchShots: 0,
  matchTorpedoesUsed: 0,
  matchSonarUsed: 0,
  matchShipsLost: 0,
  matchFirstBlood: null,
  matchDifficulty: 'easy',

  unlockedDifficulties: computeUnlocks(career.wins),

  recordShot: (hit) => set(s => ({
    matchShots: s.matchShots + 1,
    matchHits: s.matchHits + (hit ? 1 : 0),
    totalShots: s.totalShots + 1,
    totalHits: s.totalHits + (hit ? 1 : 0),
  })),

  recordTurn: () => set(s => ({ matchTurns: s.matchTurns + 1 })),

  recordChargeUsed: (type) => set(s => ({
    matchTorpedoesUsed: type === 'torpedo' ? s.matchTorpedoesUsed + 1 : s.matchTorpedoesUsed,
    matchSonarUsed: type === 'sonar' ? s.matchSonarUsed + 1 : s.matchSonarUsed,
  })),

  recordShipLost: () => set(s => ({ matchShipsLost: s.matchShipsLost + 1 })),

  recordFirstBlood: (who) => set(s => ({
    matchFirstBlood: s.matchFirstBlood === null ? who : s.matchFirstBlood,
  })),

  recordGameEnd: (won, difficulty) => {
    set(s => {
      const wins = { ...s.wins }
      const losses = { ...s.losses }
      if (won) {
        wins[difficulty]++
      } else {
        losses[difficulty]++
      }
      const currentStreak = won ? s.currentStreak + 1 : 0
      const bestStreak = Math.max(s.bestStreak, currentStreak)

      return {
        gamesPlayed: s.gamesPlayed + 1,
        wins,
        losses,
        currentStreak,
        bestStreak,
        unlockedDifficulties: computeUnlocks(wins),
      }
    })
    persistCareer(get())
  },

  resetMatch: (difficulty) => set({
    matchTurns: 0,
    matchHits: 0,
    matchShots: 0,
    matchTorpedoesUsed: 0,
    matchSonarUsed: 0,
    matchShipsLost: 0,
    matchFirstBlood: null,
    matchDifficulty: difficulty,
  }),

  checkUnlocks: () => set(s => ({
    unlockedDifficulties: computeUnlocks(s.wins),
  })),
}))
